import { BrowserSessionApiClient } from './browserSessionApiClient';
import { getCurrentReturnTo, tryStandbyReauth } from '@/utils/standbySwaReauth';
import type { ApiRequestOptions } from './coreApiClient';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/+$/, '');

export type CompanyAiCitation = {
  sourceId: number;
  title: string;
  fileName: string | null;
  filePath: string | null;
  excerpt: string;
  score: number | null;
  url: string | null;
};

export type CompanyAiSuggestedAction = {
  commandName: string;
  title: string;
  reason: string;
  payload: Record<string, unknown>;
};

export type CompanyAiPmContextItem = {
  domain: 'projects' | 'tasks' | 'invoices' | 'quotations' | 'clients' | 'certificates' | 'byda';
  recordId: number | null;
  title: string;
  summary: string;
  routePath: string | null;
};

export type CompanyAiChatSurface = 'dashboard' | 'workspace';
export type CompanyAiIntentPriority = 'high' | 'medium' | 'low';
export type CompanyAiAnswerConfidence = 'high' | 'medium' | 'low';
export type CompanyAiRoutingPrimaryIntent =
  | 'live_status_lookup'
  | 'record_lookup'
  | 'file_lookup'
  | 'process_knowledge'
  | 'mixed_lookup'
  | 'summary_report'
  | 'navigation_help'
  | 'clarification_needed';

export type CompanyAiRoutingIntent = {
  primaryIntent: CompanyAiRoutingPrimaryIntent;
  domains: Array<'projects' | 'tasks' | 'invoices' | 'quotations' | 'knowledge_files'>;
  entities: {
    projectNumbers: string[];
    invoiceNumbers: string[];
    quotationNumbers: string[];
    taskNumbers: string[];
    clientNames: string[];
    internalUserNames?: string[];
    suburbs: string[];
    fileHints: string[];
    folderHints: string[];
    keywords: string[];
  };
  needsClarification: boolean;
  clarifyingQuestion: string | null;
  liveDataPriority: CompanyAiIntentPriority;
  knowledgePriority: CompanyAiIntentPriority;
  searchPhrases: {
    liveData: string[];
    knowledge: string[];
  };
};

export type CompanyAiChatSection = {
  heading: 'Live PMapp data' | 'Company files' | 'What this means' | 'Need more detail';
  body: string;
};

export type CompanyAiUsage = {
  aiUserQuotaId: number | null;
  ownerEmail: string;
  ownerUserId: number | null;
  monthlyTokenLimit: number;
  usedTokensThisPeriod: number;
  remainingTokensThisPeriod: number;
  isEnabled: boolean;
  notes: string | null;
  periodKey: string;
};

export type CompanyAiPmQueryModelSize = 'mini' | 'gpt5';
export type CompanyAiPmQueryIntentProvider = 'deterministic' | 'openai' | 'deepseek';

export type CompanyAiModelOption = {
  value: CompanyAiPmQueryModelSize;
  label: string;
  model: string;
  description: string;
};

export type CompanyAiIntentProviderOption = {
  value: CompanyAiPmQueryIntentProvider;
  label: string;
  model: string | null;
  description: string;
};

export type CompanyAiModelSettings = {
  pmQueryModelSize: CompanyAiPmQueryModelSize;
  pmQueryModel: string;
  pmQueryIntentProvider: CompanyAiPmQueryIntentProvider;
  pmQueryIntentModel: string | null;
  options: CompanyAiModelOption[];
  intentProviderOptions: CompanyAiIntentProviderOption[];
  usesSameOpenAiApiKey: boolean;
};

export type CompanyAiGraphVisualNode = {
  id: string;
  entityNodeId: number;
  entityType: string;
  entityId: string;
  businessKey: string | null;
  displayName: string | null;
  updatedUtc?: string | null;
  degree: number;
  needsRepair: boolean;
};

export type CompanyAiGraphVisualEdge = {
  id: string;
  from: string;
  to: string;
  relationType: string;
  confidence: number;
  source: string;
  evidence: Record<string, unknown> | null;
  needsRepair: boolean;
};

export type CompanyAiGraphVisualization = {
  nodes: CompanyAiGraphVisualNode[];
  edges: CompanyAiGraphVisualEdge[];
  summary: {
    nodeCount: number;
    edgeCount: number;
    nodeTypes: Array<{ entityType: string; count: number }>;
    relationTypes: Array<{ relationType: string; count: number }>;
  };
};

export type CompanyAiConversationSummary = {
  aiConversationId: number;
  title: string;
  status: string;
  sourceScope: string;
  lastProvider: string | null;
  lastModel: string | null;
  lastMessageAt: string | null;
  createdDate: string;
};

export type CompanyAiPmRetrievalPathDebug = {
  attempted: boolean;
  matched: boolean;
  itemCount: number;
  error: string | null;
};

export type CompanyAiPmPersonAnchorBiasDebug = {
  anchor: string;
  recentClientMatch: boolean;
  recentOwnerMatch: boolean;
  bias: 'internal_user' | 'client' | 'ambiguous';
};

export type CompanyAiPmRetrievalDebug = {
  queries: string[];
  preferredDomains: Array<'projects' | 'tasks' | 'invoices' | 'quotations'>;
  pmIndex: CompanyAiPmRetrievalPathDebug;
  directProjectLookup: CompanyAiPmRetrievalPathDebug;
  relationEnrichment: CompanyAiPmRetrievalPathDebug;
  personAnchorBias?: CompanyAiPmPersonAnchorBiasDebug[];
};

export type CompanyAiChatDebug = {
  pmRetrieval: CompanyAiPmRetrievalDebug | null;
};

export type CompanyAiConversationMessage = {
  aiMessageId: number;
  role: string;
  messageText: string;
  citations: CompanyAiCitation[];
  pmContext: CompanyAiPmContextItem[];
  suggestedActions: CompanyAiSuggestedAction[];
  usage: {
    provider: string;
    model: string;
    promptTokens: number;
    outputTokens: number;
    totalTokens: number;
  } | null;
  provider: string | null;
  model: string | null;
  createdDate: string;
};

export type CompanyAiConversationDetail = {
  conversation: CompanyAiConversationSummary;
  messages: CompanyAiConversationMessage[];
};

export type CompanyAiChatResult = {
  aiConversationId: number;
  answer: string;
  citations: CompanyAiCitation[];
  pmContext: CompanyAiPmContextItem[];
  suggestedActions: CompanyAiSuggestedAction[];
  intent: CompanyAiRoutingIntent;
  confidence: CompanyAiAnswerConfidence;
  needsClarification: boolean;
  clarifyingQuestion: string | null;
  sections: CompanyAiChatSection[];
  knowledgeFiles: Array<{
    sourceId: number;
    sourceTitle: string;
    fileName: string | null;
    filePath: string | null;
    url: string | null;
    score: number;
    excerptCount: number;
  }>;
  usedEvidence: {
    pmContextCount: number;
    citationCount: number;
    knowledgeFileCount: number;
  };
  usage: {
    provider: string;
    model: string;
    promptTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  debug: CompanyAiChatDebug | null;
  quota: CompanyAiUsage;
};

export type CompanyAiKnowledgeSource = {
  aiKnowledgeSourceId: number;
  sourceType: string;
  scopeType: string;
  scopeRef: string | null;
  title: string;
  description: string | null;
  sharePointSiteId: string | null;
  driveId: string | null;
  folderPath: string | null;
  filePath: string | null;
  draftFileId: string | null;
  sourceUrl: string | null;
  status: string;
  lastIndexedAt: string | null;
  lastChecksum: string | null;
  lastError: string | null;
  isEnabled: boolean;
  selectedFileCount: number;
  pdfFileCount: number;
  totalFileBytes: number;
  createdDate: string;
  modifiedDate: string;
};

export type CompanyAiKnowledgeSourceFile = {
  aiKnowledgeSourceFileId: number;
  aiKnowledgeSourceId: number;
  fileName: string;
  relativePath: string | null;
  sourceFilePath: string | null;
  sourceItemId: string | null;
  sourceDriveId: string | null;
  sourceUrl: string | null;
  blobName: string;
  blobUrl: string | null;
  contentType: string | null;
  sizeBytes: number;
  isPdf: boolean;
  isSelected: boolean;
  createdDate: string;
  modifiedDate: string;
  contentPath: string;
};

export type CompanyAiSharePointBrowserItem = {
  itemId: string;
  name: string;
  path: string;
  webUrl: string | null;
  isFolder: boolean;
  isFile: boolean;
  contentType: string | null;
  sizeBytes: number;
  isPdf: boolean;
};

export type CompanyAiSharePointBrowserResult = {
  sharePointSiteId: string | null;
  driveId: string;
  currentFolderPath: string | null;
  currentFolderWebUrl: string | null;
  parentFolderPath: string | null;
  items: CompanyAiSharePointBrowserItem[];
};

export type CompanyAiSharePointFolderFile = {
  itemId: string;
  fileName: string;
  relativePath: string;
  sourceFilePath: string;
  webUrl: string | null;
  contentType: string | null;
  sizeBytes: number;
  isPdf: boolean;
};

export type CompanyAiKnowledgeFile = {
  sourceId: number;
  sourceTitle: string;
  sourceType: string;
  scopeType: string;
  fileName: string | null;
  filePath: string | null;
  url: string | null;
  chunkCount: number;
  lastIndexedAt: string | null;
};

export type CompanyAiOnlineQuoteFile = {
  referralLeadFileId: number;
  referralLeadId: number;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  storageProvider: string | null;
  storagePath: string | null;
  draftFileId: string | null;
  blobName?: string | null;
  groupPath?: string | null;
  isPrimary: boolean;
  uploadedDate: string | null;
  lastModified?: string | null;
  contentPath: string | null;
};

export type CompanyAiOnlineQuoteFileGroup = {
  referralLeadId: number;
  requestCode: string | null;
  submittedDate: string | null;
  uploadUserName?: string | null;
  uploadUserEmail?: string | null;
  siteAddress?: string | null;
  clientName: string | null;
  clientEmail: string | null;
  projectAddress: string | null;
  suburb: string | null;
  postCode: number | null;
  council: string | null;
  referralStatus: string | null;
  reviewStatus: string | null;
  portalUserEmail: string | null;
  quotationId: number | null;
  projectId: number | null;
  phone?: string | null;
  projectType?: string | null;
  customerType?: string | null;
  helpType?: string | null;
  notes?: string | null;
  spamScore?: number | null;
  spamReasons?: string[];
  files: CompanyAiOnlineQuoteFile[];
};

export type CompanyAiQuotationDescriptionResult = {
  description: string | null;
  source: 'deepseek' | 'fallback';
  model: string | null;
};

export type CompanyAiGroundingPreview = {
  sourceScope: 'company' | 'project' | 'selected';
  selectedSourceIds: number[];
  citations: CompanyAiCitation[];
  pmContext: CompanyAiPmContextItem[];
  knowledgeFiles: Array<{
    sourceId: number;
    sourceTitle: string;
    fileName: string | null;
    filePath: string | null;
    url: string | null;
    score: number;
    excerptCount: number;
  }>;
  intent: CompanyAiRoutingIntent | null;
};

export type CompanyAiSearchResult = {
  answer: string;
  sections: Array<{
    heading: 'Answer' | 'Evidence' | 'Need more detail';
    body: string;
  }>;
  intent: {
    intent: string;
    entities: Record<string, unknown>;
    confidence: number;
    ambiguity: string[] | null;
    clarifyingQuestion: string | null;
    dataSources: string[];
  };
  plan: {
    intent: string;
    entities: Record<string, unknown>;
    dataSources: string[];
    operations: Array<{ type: string; args?: Record<string, unknown> }>;
    outputShape: string;
    ambiguity: string[] | null;
    clarifyingQuestion: string | null;
  };
  ambiguity: string[] | null;
  clarifyingQuestion: string | null;
  evidence: Array<{
    evidenceType: string;
    label: string;
    summary: string;
    tableName: string | null;
    recordId: number | null;
    documentId: number | null;
    sectionId: number | null;
    blockId: number | null;
    documentTitle: string | null;
    sectionHeading: string | null;
    pageNumber: number | null;
    referencePath: string | null;
    score: number | null;
    confidence: number | null;
    retrievedAt: string;
  }>;
  tablesUsed: string[];
  recordIds: number[];
  documentsUsed: number[];
  retrievedAt: string;
  surface: CompanyAiChatSurface;
};

export type CompanyAiEntityType = 'project' | 'client' | 'task' | 'invoice' | 'quotation' | 'certificate' | 'byda';

export type CompanyAiQueryCandidate = {
  entityType: CompanyAiEntityType;
  entityId: number;
  routePath: string | null;
  title: string;
  subtitle: string;
  projectNumber?: string | null;
  quotationNumber?: string | null;
  invoiceNumber?: string | null;
  taskNumber?: string | null;
  address?: string | null;
  suburb?: string | null;
  status?: string | null;
  score?: number;
  confidence?: number;
  scoreBreakdown?: Array<{
    reason: string;
    score: number;
    route?: string | null;
    matchedTokens?: string[];
  }>;
};

export type CompanyAiQueryResult = {
  sessionId: string;
  actionType: 'answer' | 'clarify' | 'candidate_list';
  answer: string;
  confidence: number | null;
  clarificationQuestion: string | null;
  candidates: CompanyAiQueryCandidate[];
  selectedEntityType: CompanyAiEntityType | null;
  selectedEntityId: number | null;
  stateSummary: {
    currentScope: CompanyAiEntityType | 'mixed' | null;
    clarificationCount: number;
    selectedEntityType: CompanyAiEntityType | null;
    selectedEntityId: number | null;
    candidateCount: number;
  };
  plan: {
    normalizedQuery: string;
    entityTypeHints: string[];
    intent: string;
    constraints: Record<string, unknown>;
    dateRange: {
      startDate: string | null;
      endDate: string | null;
      dateField: string | null;
      sourcePhrase: string | null;
    };
    relationHops: string[];
    sortPreference: string;
  };
  agentSteps?: CompanyAiAgentExecutionStep[];
  agentData?: CompanyAiAgentData;
  citations?: Array<{
    sourceType: 'live_pmapp' | 'draft';
    label: string;
    detail: string | null;
    routePath: string | null;
  }>;
};

export type CompanyAiQuerySessionDetail = {
  sessionId: string;
  ownerEmail: string;
  ownerUserId: number | null;
  originalQuery: string;
  scopeStatus: string;
  state: {
    sessionId: string;
    originalQuery: string;
    currentScope: CompanyAiEntityType | 'mixed' | null;
    clarificationCount: number;
    confidence: number | null;
    selectedEntityType: CompanyAiEntityType | null;
    selectedEntityId: number | null;
    candidatePool: CompanyAiQueryCandidate[];
  };
  clarificationCount: number;
  finalConfidence: number | null;
  startedUtc: string | null;
  lastUpdatedUtc: string | null;
  resolvedEntityType: CompanyAiEntityType | null;
  resolvedEntityId: number | null;
  turns: Array<{
    turnIndex: number;
    userMessage: string;
    systemActionType: string | null;
    clarificationQuestion: string | null;
    chosenEntityType: string | null;
    chosenEntityId: number | null;
    confidence: number | null;
    createdUtc: string | null;
  }>;
};

export type CompanyAiAgentToolName =
  | 'parse'
  | 'resolve_entity'
  | 'resolve_time'
  | 'resolve_actor'
  | 'plan'
  | 'fetch'
  | 'clarify'
  | 'search_projects'
  | 'get_project_detail'
  | 'search_clients'
  | 'get_client_detail'
  | 'search_tasks'
  | 'get_task_detail'
  | 'search_invoices'
  | 'summarize_invoices'
  | 'get_invoice_detail'
  | 'search_quotations'
  | 'get_quotation_detail'
  | 'search_certificates'
  | 'get_certificate_detail'
  | 'search_byda'
  | 'get_byda_detail'
  | 'knowledge_lookup';

export type CompanyAiAgentCurrentEntity = {
  type: string;
  id: string | number;
  label?: string;
};

export type CompanyAiAgentProjectRecord = {
  projectId: number;
  projectNumber: string | null;
  title: string;
  summary: string;
  routePath: string | null;
  clientName: string | null;
  status: string | null;
  address: string | null;
  suburb: string | null;
  quotationId: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type CompanyAiAgentClientRecord = {
  clientId: number;
  name: string | null;
  company: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  matchScore: number | null;
};

export type CompanyAiAgentInvoiceRecord = {
  invoiceId: number;
  invoiceNumber: string | null;
  projectId: number | null;
  projectNumber: string | null;
  projectAddress: string | null;
  projectSuburb: string | null;
  clientName: string | null;
  amount: number | null;
  status: string | null;
  paymentStatus: string | null;
  issueDate: string | null;
  invoiceDate?: string | null;
  sentDate?: string | null;
  dueDate: string | null;
  paidDate: string | null;
  stripePaymentUrl: string | null;
  senderEmail: string | null;
  entity: string | null;
  linkedQuotationId?: number | null;
  isUnpaid: boolean;
  isOverdue: boolean;
};

export type CompanyAiAgentQuotationRecord = {
  quotationId: number;
  quotationNumber: string | null;
  projectId: number | null;
  title: string;
  summary: string;
  routePath: string | null;
  clientName: string | null;
  status: string | null;
  quotationDate?: string | null;
  createdDate: string | null;
  modifiedDate: string | null;
  linkedInvoiceIds?: number[];
};

export type CompanyAiAgentTaskRecord = {
  taskId: number;
  taskNumber: string | null;
  title: string;
  summary: string;
  routePath: string | null;
  projectId: number | null;
  projectNumber: string | null;
  clientName: string | null;
  status: string | null;
  dueDate: string | null;
  completedDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  assignedTo: string | null;
  assignedToUserId: number | null;
  createdBy: string | null;
  createdByUserId: number | null;
  ownerUserId: number | null;
  rfiTicketNumber: string | null;
  isOverdue: boolean;
  isDueSoon: boolean;
};

export type CompanyAiAgentCertificateRecord = {
  certificateId: number;
  certificateNumber: string | null;
  projectId: number | null;
  projectNumber: string | null;
  title: string;
  summary: string;
  routePath: string | null;
  clientName: string | null;
  status: string | null;
  issueDate: string | null;
  address: string | null;
  suburb: string | null;
  certificateType: string | null;
  source: string | null;
};

export type CompanyAiAgentBydaRecord = {
  bydaEnquiryId: number;
  userReference: string | null;
  projectId: number | null;
  projectNumber: string | null;
  projectName: string | null;
  address: string | null;
  suburb: string | null;
  localStatus: string | null;
  remoteEnquiryId: number | null;
  remoteExternalId: string | null;
  digStartAt: string | null;
  digEndAt: string | null;
  shareUrl: string | null;
  lastSyncedAt: string | null;
};

export type CompanyAiAgentQuerySpec = {
  primaryIntent?: string;
  rawQuery?: string;
  entities?: Array<{ kind: string; rawText: string; resolvedId?: string; resolvedLabel?: string; confidence?: number }>;
  semanticFrame?: Record<string, unknown>;
  canonicalQuery?: Record<string, unknown>;
  clarificationState?: Record<string, unknown>;
  temporal?: Array<{ field?: string; start?: string; end?: string; relativeLabel?: string; rawText?: string }>;
  filters?: Record<string, unknown>;
  relations?: Record<string, unknown>;
  aggregation?: { groupBy?: string; sortBy?: string; sortDir?: string };
  explicitDocumentIntent?: boolean;
  needsClarification?: boolean;
  clarificationReason?: string;
};

export type CompanyAiAgentDraftEmail = {
  projectId: number;
  invoiceIds: number[];
  primaryInvoiceId: number | null;
  subject: string;
  html: string;
  notes: string[];
  notSent: true;
};

export type CompanyAiAgentInvoiceSummary = {
  title: string;
  summary: string;
  routePath: string | null;
  measure: string;
  aggregation: string;
  periodLabel: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  dateField: string | null;
  totalInvoicedAmount: number;
  totalReceivables: number;
  overdueReceivables: number;
  paidAmount: number;
  totalInvoiceCount: number;
  openInvoiceCount: number;
  overdueInvoiceCount: number;
  paidInvoiceCount: number;
  averageInvoiceAmount: number;
  averageReceivableAmount: number;
  averageOverdueAmount: number;
  averagePaidAmount: number;
};

export type CompanyAiAgentExecutionStep = {
  type?: CompanyAiAgentToolName | string;
  tool: CompanyAiAgentToolName | string;
  success?: boolean;
  summary: string;
  inputSummary?: string;
  outputSummary?: string;
  confidence?: number;
};

export type CompanyAiAgentData = {
  matches?: CompanyAiQueryCandidate[];
  querySpec?: CompanyAiAgentQuerySpec;
  resolvedProject?: CompanyAiAgentProjectRecord;
  resolvedClient?: CompanyAiAgentClientRecord;
  resolvedTask?: CompanyAiAgentTaskRecord;
  resolvedInvoice?: CompanyAiAgentInvoiceRecord;
  invoiceSummary?: CompanyAiAgentInvoiceSummary;
  resolvedQuotation?: CompanyAiAgentQuotationRecord;
  resolvedCertificate?: CompanyAiAgentCertificateRecord;
  resolvedByda?: CompanyAiAgentBydaRecord;
  projectOptions?: CompanyAiAgentProjectRecord[];
  clientOptions?: CompanyAiAgentClientRecord[];
  taskOptions?: CompanyAiAgentTaskRecord[];
  invoiceOptions?: CompanyAiAgentInvoiceRecord[];
  quotationOptions?: CompanyAiAgentQuotationRecord[];
  certificateOptions?: CompanyAiAgentCertificateRecord[];
  bydaOptions?: CompanyAiAgentBydaRecord[];
  projects?: CompanyAiAgentProjectRecord[];
  clients?: CompanyAiAgentClientRecord[];
  tasks?: CompanyAiAgentTaskRecord[];
  invoices?: CompanyAiAgentInvoiceRecord[];
  quotations?: CompanyAiAgentQuotationRecord[];
  certificates?: CompanyAiAgentCertificateRecord[];
  byda?: CompanyAiAgentBydaRecord[];
  knowledge?: Array<{
    documentId: number;
    title: string;
    summary: string;
    filePath: string | null;
    url: string | null;
  }>;
  draftEmail?: CompanyAiAgentDraftEmail;
  groupings?: Array<{
    groupBy: 'client' | 'project' | 'status' | 'assignee';
    label: string;
    count: number;
  }>;
};

export type CompanyAiAgentRunResult =
  | {
      sessionId: string;
      type: 'clarification';
      answer: string;
      data?: CompanyAiAgentData;
      steps?: CompanyAiAgentExecutionStep[];
    }
  | {
      sessionId: string;
      type: 'answer';
      answer: string;
      data?: CompanyAiAgentData;
      steps?: CompanyAiAgentExecutionStep[];
    };

export type CompanyKnowledgeDocumentSummary = {
  knowledgeDocumentId: number;
  aiKnowledgeSourceId: number;
  latestKnowledgeIngestionRunId: number | null;
  title: string;
  fileName: string | null;
  documentType: string | null;
  documentTypeConfidence: number | null;
  businessDomain: string | null;
  businessDomainConfidence: number | null;
  versionText: string | null;
  issueDate: string | null;
  authorOwner: string | null;
  sourceFilePath: string | null;
  sourceBlobUrl: string | null;
  totalPages: number;
  documentSummary: string | null;
  sectionSummaryText: string | null;
  topics: string[];
  isActive: boolean;
  isDeleted: boolean;
  createdDate: string;
  modifiedDate: string;
};

export type CompanyKnowledgeDocumentStructure = {
  document: CompanyKnowledgeDocumentSummary;
  sections: Array<{
    knowledgeSectionId: number;
    parentKnowledgeSectionId: number | null;
    sectionKey: string;
    headingText: string;
    headingLevel: number;
    pageStart: number | null;
    pageEnd: number | null;
    sectionOrder: number;
    canonicalSectionPath: string | null;
    sectionSummary: string | null;
    detectionConfidence: number | null;
  }>;
  blocks: Array<{
    knowledgeBlockId: number;
    knowledgeSectionId: number | null;
    pageNumber: number | null;
    blockOrder: number;
    blockType: string;
    cleanedText: string;
    shortSummary: string | null;
    keywordsText: string | null;
    pageAnchor: string | null;
    blockConfidence: number | null;
  }>;
  entities: Array<{
    knowledgeEntityId: number;
    knowledgeSectionId: number | null;
    knowledgeBlockId: number | null;
    entityType: string;
    entityValue: string;
    normalizedValue: string | null;
    confidence: number | null;
  }>;
  keywords: Array<{
    knowledgeKeywordId: number;
    knowledgeSectionId: number | null;
    knowledgeBlockId: number | null;
    keyword: string;
    normalizedKeyword: string;
    score: number | null;
  }>;
  rules: Array<{
    knowledgeRuleId: number;
    knowledgeSectionId: number | null;
    knowledgeBlockId: number | null;
    ruleCategory: string | null;
    ruleText: string;
    responsibility: string | null;
    deadlineText: string | null;
    confidence: number | null;
  }>;
  workflows: Array<{
    knowledgeWorkflowId: number;
    knowledgeSectionId: number | null;
    knowledgeBlockId: number | null;
    workflowName: string | null;
    stepOrder: number;
    stepText: string;
    responsibility: string | null;
    durationText: string | null;
    confidence: number | null;
  }>;
};

export type CompanyKnowledgeSearchResult = {
  documents: CompanyKnowledgeDocumentSummary[];
  evidence: CompanyAiSearchResult['evidence'];
};

export type CompanyAiFilePreview = {
  aiFilePreviewId: number;
  draftFileId: string;
  ownerEmail: string;
  status: string;
  projectId: number | null;
  quotationId: number | null;
  confidence: number;
  fileClass: string | null;
  suggestedDriveId: string | null;
  suggestedFolderPath: string | null;
  suggestedFileName: string | null;
  extractedSummary: string | null;
  routingDetails: Record<string, unknown> | null;
  commitResult: Record<string, unknown> | null;
  createdDate: string;
  modifiedDate: string;
  canAutoCommit?: boolean;
  matchedProject?: {
    projectId: number;
    projectNumber: string | null;
    title: string | null;
  } | null;
};

export type CompanyEmailReviewStatus = 'pending' | 'accepted' | 'rejected';
export type CompanyEmailTaskCreationStatus = 'not_created' | 'created' | 'failed';
export type CompanyEmailBehaviorDisposition = 'likely_reply' | 'likely_ignore' | 'likely_action_only' | 'undetermined';
export type CompanyEmailBehaviorReviewPriority = 'high' | 'medium' | 'low';

export type CompanyEmailReviewListItem = {
  emailMessageId: number;
  sourceSystem: string;
  sourceMailbox: string | null;
  externalMessageId: string | null;
  internetMessageId: string | null;
  conversationId: string | null;
  threadId: string | null;
  messageDirection: 'inbound' | 'outbound' | 'unknown';
  folderName: string | null;
  inReplyToInternetMessageId: string | null;
  subject: string;
  fromEmail: string | null;
  fromName: string | null;
  receivedAt: string | null;
  bodyPreview: string | null;
  classification: string | null;
  classificationConfidence: number | null;
  processingStatus: string;
  reviewStatus: CompanyEmailReviewStatus;
  ingestedAt: string | null;
  lastProcessedAt: string | null;
  topProjectSuggestion: {
    projectId: number | null;
    confidence: number;
  } | null;
  behaviorSignal: {
    disposition: CompanyEmailBehaviorDisposition;
    confidence: number;
    requiresReview: boolean;
    reviewPriority: CompanyEmailBehaviorReviewPriority;
    observedSampleSize: number;
    avgReplyMinutes: number | null;
    reasons: string[];
    matchedPatterns: Array<{
      scope: string;
      label: string;
      sampleSize: number;
      replyRate: number;
      ignoreRate: number;
      actionOnlyRate: number;
      avgReplyMinutes: number | null;
      lastMatchedAt: string | null;
    }>;
  } | null;
  suggestionCounts: {
    projects: number;
    tasks: number;
    files: number;
  };
};

export type CompanyEmailReviewDetail = {
  message: CompanyEmailReviewListItem & {
    toRecipients: Array<{ email: string; name?: string | null }>;
    ccRecipients: Array<{ email: string; name?: string | null }>;
    normalizedBody: string | null;
    rawMetadata: Record<string, unknown> | null;
    reviewNotes: string | null;
    references: string[];
  };
  attachments: Array<{
    emailMessageAttachmentId: number;
    externalAttachmentId: string | null;
    name: string;
    contentType: string | null;
    size: number | null;
    isInline: boolean;
    metadata: Record<string, unknown> | null;
  }>;
  processingRuns: Array<{
    emailProcessingRunId: number;
    stage: string;
    status: string;
    processorVersion: string | null;
    metrics: Record<string, unknown> | null;
    errorSummary: string | null;
    startedAt: string | null;
    completedAt: string | null;
  }>;
  projectSuggestions: Array<{
    emailProjectSuggestionId: number;
    projectId: number | null;
    confidence: number;
    rank: number;
    reasonCodes: string[];
    evidence: Array<{ source: string; text: string }>;
    candidateEntityType: string | null;
    candidateEntityId: number | null;
    matchedBy: string[];
    graphEvidence?: Array<{
      label: string;
      relationPath: string;
      confidence: number;
      sourceEntityType: string;
      sourceEntityId: string;
      sourceDisplayName: string | null;
      details?: Record<string, unknown> | null;
    }>;
    title: string | null;
    projectNumber: string | null;
    reviewStatus: CompanyEmailReviewStatus;
    reviewedBy: string | null;
    reviewedAt: string | null;
    reviewNotes: string | null;
  }>;
  taskSuggestions: Array<{
    emailTaskSuggestionId: number;
    suggestedProjectId: number | null;
    suggestedTitle: string;
    suggestedDescription: string | null;
    suggestedDueDate: string | null;
    suggestedPriority: string | null;
    suggestedOwner: string | null;
    confidence: number;
    rank: number;
    reasonCodes: string[];
    evidence: Array<{ source: string; text: string }>;
    reviewStatus: CompanyEmailReviewStatus;
    reviewedBy: string | null;
    reviewedAt: string | null;
    reviewNotes: string | null;
    createdTaskId: number | null;
    createdTaskAt: string | null;
    createdTaskBy: string | null;
    taskCreationStatus: CompanyEmailTaskCreationStatus;
    taskCreationError: string | null;
    canCreateTask: boolean;
    taskCreationBlockedReason: string | null;
  }>;
  fileSuggestions: Array<{
    emailFileSuggestionId: number;
    emailMessageAttachmentId: number | null;
    attachmentName: string;
    attachmentContentType: string | null;
    suggestedProjectId: number | null;
    suggestedQuotationId?: number | null;
    suggestedQuotationNumber?: string | null;
    suggestedFolder: string | null;
    suggestedCategory: string | null;
    suggestedFileName?: string | null;
    confidence: number;
    rank: number;
    reasonCodes: string[];
    evidence: Array<{ source: string; text: string }>;
    reviewStatus: CompanyEmailReviewStatus;
    reviewedBy: string | null;
    reviewedAt: string | null;
    reviewNotes: string | null;
  }>;
  threadMessages: Array<{
    emailMessageId: number;
    subject: string;
    fromEmail: string | null;
    fromName: string | null;
    receivedAt: string | null;
    messageDirection: 'inbound' | 'outbound' | 'unknown';
    classification: string | null;
    reviewStatus: CompanyEmailReviewStatus;
    outcomeTypes: string[];
  }>;
  replyPairs: Array<{
    emailReplyPairId: number;
    inboundEmailMessageId: number;
    replyEmailMessageId: number;
    pairingMethod: string;
    pairingConfidence: number;
    pairedAt: string | null;
    inboundMessage: {
      emailMessageId: number;
      subject: string;
      fromEmail: string | null;
      fromName: string | null;
      receivedAt: string | null;
      messageDirection: 'inbound' | 'outbound' | 'unknown';
      classification: string | null;
      reviewStatus: CompanyEmailReviewStatus;
      outcomeTypes: string[];
    } | null;
    replyMessage: {
      emailMessageId: number;
      subject: string;
      fromEmail: string | null;
      fromName: string | null;
      receivedAt: string | null;
      messageDirection: 'inbound' | 'outbound' | 'unknown';
      classification: string | null;
      reviewStatus: CompanyEmailReviewStatus;
      outcomeTypes: string[];
    } | null;
  }>;
  outcomes: Array<{
    emailMessageOutcomeId: number;
    outcomeType: string;
    outcomeStatus: string;
    relatedEmailMessageId: number | null;
    relatedEntityType: string | null;
    relatedEntityId: number | null;
    evidence: Record<string, unknown> | null;
    recordedAt: string | null;
  }>;
  reviewerFeedback: Array<{
    emailReviewerFeedbackId: number;
    emailMessageId: number;
    suggestionKind: 'email' | 'project' | 'task' | 'file';
    suggestionRecordId: number | null;
    reviewStatus: CompanyEmailReviewStatus;
    reviewedBy: string | null;
    reviewedAt: string | null;
    reviewNotes: string | null;
    sourceMailbox: string | null;
    senderEmail: string | null;
    classification: string | null;
    projectId: number | null;
    suggestedTitle: string | null;
    suggestedCategory: string | null;
    signals: Record<string, unknown> | null;
  }>;
};

export type CompanyEmailDraftAssistance = {
  humanReviewRequired: true;
  generatedAt: string;
  internalSummary: string;
  replyDraft: {
    subject: string;
    body: string;
  };
  supervision: {
    exampleCount: number;
    learnedGreeting: string | null;
    learnedSignOff: string | null;
    toneNotes: string[];
    examples: Array<{
      inboundEmailMessageId: number;
      replyEmailMessageId: number;
      inboundSubject: string;
      replySubject: string;
      matchScope: string;
      pairingMethod: string;
      replyExcerpt: string;
      pairedAt: string | null;
    }>;
  };
  suggestedNextAction: {
    title: string;
    description: string;
    projectId: number | null;
  } | null;
  workflowHandoffs: Array<{
    handoffType: 'task_draft' | 'reminder_draft' | 'quote_follow_up_draft' | 'invoice_follow_up_draft';
    title: string;
    description: string;
    reason: string;
    targetArea: 'tasks' | 'projects' | 'quotations' | 'invoices';
    projectId: number | null;
    recommended: boolean;
    requiresHumanReview: true;
    payload: Record<string, unknown>;
  }>;
  guidance: string[];
};

export type CompanyEmailReviewListResult = {
  items: CompanyEmailReviewListItem[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export type CompanyEmailTaskCreateResult = {
  emailTaskSuggestionId: number;
  emailMessageId: number;
  createdTaskId: number;
  createdTaskAt: string | null;
  createdTaskBy: string | null;
  taskCreationStatus: CompanyEmailTaskCreationStatus;
  taskCreationError: string | null;
  alreadyCreated: boolean;
};

export type CompanyEmailReprocessResult = {
  emailMessageId: number;
  classification: string;
  classificationConfidence: number;
  projectSuggestionCount: number;
  taskSuggestionCount: number;
  fileSuggestionCount: number;
  topProjectId: number | null;
  topProjectConfidence: number | null;
  matchedEntityType: string | null;
  matchedEntityId: number | null;
  decisionStatus: string;
  decisionSource: string;
};

function buildIdempotencyKey(prefix: string): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${random}`;
}

function unwrapData<T>(value: any): T {
  return (value?.data ?? value) as T;
}

function asNonEmptyString(value: unknown): string | null {
  const normalized = String(value || '').trim();
  return normalized || null;
}

function buildAgentCandidateFromProject(project: CompanyAiAgentProjectRecord): CompanyAiQueryCandidate {
  return {
    entityType: 'project',
    entityId: project.projectId,
    routePath: project.routePath,
    title: project.title,
    subtitle: project.summary,
    projectNumber: project.projectNumber,
    address: project.address,
    suburb: project.suburb,
    status: project.status,
    confidence: 0.78,
  };
}

function buildAgentCandidateFromInvoice(invoice: CompanyAiAgentInvoiceRecord): CompanyAiQueryCandidate {
  return {
    entityType: 'invoice',
    entityId: invoice.invoiceId,
    routePath: '/invoices',
    title: invoice.invoiceNumber ? `Invoice ${invoice.invoiceNumber}` : `Invoice #${invoice.invoiceId}`,
    subtitle: [
      invoice.clientName ? `client ${invoice.clientName}` : null,
      invoice.projectNumber ? `project ${invoice.projectNumber}` : null,
      invoice.dueDate ? `due ${invoice.dueDate}` : null,
      invoice.paymentStatus ? `payment ${invoice.paymentStatus}` : null,
    ].filter(Boolean).join('; '),
    invoiceNumber: invoice.invoiceNumber,
    status: invoice.status || invoice.paymentStatus,
    confidence: 0.78,
  };
}

function buildAgentCandidateFromClient(client: CompanyAiAgentClientRecord): CompanyAiQueryCandidate {
  return {
    entityType: 'client',
    entityId: client.clientId,
    routePath: '/clients',
    title: client.company || client.name || `Client ${client.clientId}`,
    subtitle: [
      client.name && client.company && client.name !== client.company ? client.name : null,
      client.email,
      client.address,
    ].filter(Boolean).join('; '),
    confidence: typeof client.matchScore === 'number' ? Math.min(0.99, client.matchScore / 320) : 0.72,
  };
}

function buildAgentCandidateFromTask(task: CompanyAiAgentTaskRecord): CompanyAiQueryCandidate {
  return {
    entityType: 'task',
    entityId: task.taskId,
    routePath: task.routePath || '/tasks',
    title: task.taskNumber ? `Task ${task.taskNumber}` : task.title,
    subtitle: task.summary,
    taskNumber: task.taskNumber,
    status: task.status,
    confidence: 0.78,
  };
}

function buildAgentCandidateFromQuotation(quotation: CompanyAiAgentQuotationRecord): CompanyAiQueryCandidate {
  return {
    entityType: 'quotation',
    entityId: quotation.quotationId,
    routePath: quotation.routePath || '/quotations',
    title: quotation.title,
    subtitle: quotation.summary,
    quotationNumber: quotation.quotationNumber,
    status: quotation.status,
    confidence: 0.78,
  };
}

function buildAgentCandidateFromCertificate(certificate: CompanyAiAgentCertificateRecord): CompanyAiQueryCandidate {
  return {
    entityType: 'certificate',
    entityId: certificate.certificateId,
    routePath: certificate.routePath || '/certificates',
    title: certificate.certificateNumber ? `Certificate ${certificate.certificateNumber}` : certificate.title,
    subtitle: certificate.summary,
    status: certificate.status,
    address: certificate.address,
    suburb: certificate.suburb,
    confidence: 0.78,
  };
}

function buildAgentCandidateFromByda(record: CompanyAiAgentBydaRecord): CompanyAiQueryCandidate {
  return {
    entityType: 'byda',
    entityId: record.bydaEnquiryId,
    routePath: '/ai',
    title: record.userReference || record.remoteExternalId || `BYDA ${record.bydaEnquiryId}`,
    subtitle: [
      record.projectNumber ? `project ${record.projectNumber}` : null,
      record.address || record.suburb,
      record.localStatus ? `status ${record.localStatus}` : null,
    ].filter(Boolean).join('; '),
    address: record.address,
    suburb: record.suburb,
    status: record.localStatus,
    confidence: 0.78,
  };
}

function deriveAgentCandidates(result: CompanyAiAgentRunResult): CompanyAiQueryCandidate[] {
  const data = result.data;
  if (Array.isArray(data?.matches) && data.matches.length > 0) {
    return data.matches;
  }

  if (Array.isArray(data?.projectOptions) && data.projectOptions.length > 0) {
    return data.projectOptions.map(buildAgentCandidateFromProject);
  }

  if (Array.isArray(data?.clientOptions) && data.clientOptions.length > 0) {
    return data.clientOptions.map(buildAgentCandidateFromClient);
  }

  if (Array.isArray(data?.taskOptions) && data.taskOptions.length > 0) {
    return data.taskOptions.map(buildAgentCandidateFromTask);
  }

  if (Array.isArray(data?.invoiceOptions) && data.invoiceOptions.length > 0) {
    return data.invoiceOptions.map(buildAgentCandidateFromInvoice);
  }

  if (Array.isArray(data?.quotationOptions) && data.quotationOptions.length > 0) {
    return data.quotationOptions.map(buildAgentCandidateFromQuotation);
  }

  if (Array.isArray(data?.certificateOptions) && data.certificateOptions.length > 0) {
    return data.certificateOptions.map(buildAgentCandidateFromCertificate);
  }

  if (Array.isArray(data?.bydaOptions) && data.bydaOptions.length > 0) {
    return data.bydaOptions.map(buildAgentCandidateFromByda);
  }

  if (data?.resolvedProject) {
    return [buildAgentCandidateFromProject(data.resolvedProject)];
  }

  if (data?.resolvedClient) {
    return [buildAgentCandidateFromClient(data.resolvedClient)];
  }

  if (data?.resolvedTask) {
    return [buildAgentCandidateFromTask(data.resolvedTask)];
  }

  if (data?.resolvedInvoice) {
    return [buildAgentCandidateFromInvoice(data.resolvedInvoice)];
  }

  if (data?.resolvedQuotation) {
    return [buildAgentCandidateFromQuotation(data.resolvedQuotation)];
  }

  if (data?.resolvedCertificate) {
    return [buildAgentCandidateFromCertificate(data.resolvedCertificate)];
  }

  if (data?.resolvedByda) {
    return [buildAgentCandidateFromByda(data.resolvedByda)];
  }

  if (Array.isArray(data?.tasks) && data.tasks.length > 0) {
    return data.tasks.slice(0, 5).map(buildAgentCandidateFromTask);
  }

  if (Array.isArray(data?.quotations) && data.quotations.length > 0) {
    return data.quotations.slice(0, 5).map(buildAgentCandidateFromQuotation);
  }

  if (Array.isArray(data?.invoices) && data.invoices.length > 0) {
    return data.invoices.slice(0, 5).map(buildAgentCandidateFromInvoice);
  }

  if (Array.isArray(data?.certificates) && data.certificates.length > 0) {
    return data.certificates.slice(0, 5).map(buildAgentCandidateFromCertificate);
  }

  if (Array.isArray(data?.byda) && data.byda.length > 0) {
    return data.byda.slice(0, 5).map(buildAgentCandidateFromByda);
  }

  if (Array.isArray(data?.projects) && data.projects.length > 0) {
    return data.projects.slice(0, 5).map(buildAgentCandidateFromProject);
  }

  if (Array.isArray(data?.clients) && data.clients.length > 0) {
    return data.clients.slice(0, 5).map(buildAgentCandidateFromClient);
  }

  return [];
}

function deriveAgentSelectedEntity(result: CompanyAiAgentRunResult, _candidates: CompanyAiQueryCandidate[]) {
  if (result.data?.resolvedProject?.projectId) {
    return {
      selectedEntityType: 'project' as const,
      selectedEntityId: result.data.resolvedProject.projectId,
    };
  }

  if (result.data?.resolvedClient?.clientId) {
    return {
      selectedEntityType: 'client' as const,
      selectedEntityId: result.data.resolvedClient.clientId,
    };
  }

  if (result.data?.resolvedTask?.taskId) {
    return {
      selectedEntityType: 'task' as const,
      selectedEntityId: result.data.resolvedTask.taskId,
    };
  }

  if (result.data?.resolvedInvoice?.invoiceId) {
    return {
      selectedEntityType: 'invoice' as const,
      selectedEntityId: result.data.resolvedInvoice.invoiceId,
    };
  }

  if (result.data?.resolvedQuotation?.quotationId) {
    return {
      selectedEntityType: 'quotation' as const,
      selectedEntityId: result.data.resolvedQuotation.quotationId,
    };
  }

  if (result.data?.resolvedCertificate?.certificateId) {
    return {
      selectedEntityType: 'certificate' as const,
      selectedEntityId: result.data.resolvedCertificate.certificateId,
    };
  }

  if (result.data?.resolvedByda?.bydaEnquiryId) {
    return {
      selectedEntityType: 'byda' as const,
      selectedEntityId: result.data.resolvedByda.bydaEnquiryId,
    };
  }

  return {
    selectedEntityType: null,
    selectedEntityId: null,
  };
}

function pushAgentCitation(
  target: Array<{
    sourceType: 'live_pmapp' | 'draft';
    label: string;
    detail: string | null;
    routePath: string | null;
  }>,
  value: {
    sourceType: 'live_pmapp' | 'draft';
    label: string;
    detail: string | null;
    routePath: string | null;
  } | null,
  seen: Set<string>
) {
  if (!value?.label) return;
  const key = `${value.sourceType}:${value.label}:${value.routePath || ''}`;
  if (seen.has(key)) return;
  seen.add(key);
  target.push(value);
}

function deriveAgentCitations(result: CompanyAiAgentRunResult) {
  const citations: Array<{
    sourceType: 'live_pmapp' | 'draft';
    label: string;
    detail: string | null;
    routePath: string | null;
  }> = [];
  const seen = new Set<string>();
  const data = result.data;

  if (data?.resolvedProject) {
    pushAgentCitation(citations, {
      sourceType: 'live_pmapp',
      label: data.resolvedProject.title,
      detail: data.resolvedProject.summary,
      routePath: data.resolvedProject.routePath || '/projects',
    }, seen);
  }

  (data?.projectOptions || data?.projects || []).slice(0, 3).forEach((project) => {
    pushAgentCitation(citations, {
      sourceType: 'live_pmapp',
      label: project.title,
      detail: project.summary,
      routePath: project.routePath || '/projects',
    }, seen);
  });

  (data?.clients || data?.clientOptions || []).slice(0, 3).forEach((client) => {
    pushAgentCitation(citations, {
      sourceType: 'live_pmapp',
      label: client.company || client.name || `Client ${client.clientId}`,
      detail: [client.email, client.address].filter(Boolean).join(' · ') || null,
      routePath: '/clients',
    }, seen);
  });

  (data?.quotations || []).slice(0, 3).forEach((quotation) => {
    pushAgentCitation(citations, {
      sourceType: 'live_pmapp',
      label: quotation.title,
      detail: quotation.summary,
      routePath: quotation.routePath || '/quotations',
    }, seen);
  });

  (data?.invoices || []).slice(0, 3).forEach((invoice) => {
    pushAgentCitation(citations, {
      sourceType: 'live_pmapp',
      label: invoice.invoiceNumber ? `Invoice ${invoice.invoiceNumber}` : `Invoice #${invoice.invoiceId}`,
      detail: [
        invoice.clientName ? `client ${invoice.clientName}` : null,
        invoice.projectNumber ? `project ${invoice.projectNumber}` : null,
        invoice.paymentStatus ? `payment ${invoice.paymentStatus}` : null,
        invoice.dueDate ? `due ${invoice.dueDate}` : null,
      ].filter(Boolean).join(' · ') || null,
      routePath: '/invoices',
    }, seen);
  });

  if (data?.invoiceSummary) {
    pushAgentCitation(citations, {
      sourceType: 'live_pmapp',
      label: data.invoiceSummary.title || 'Invoice summary',
      detail: data.invoiceSummary.summary || null,
      routePath: data.invoiceSummary.routePath || '/invoices',
    }, seen);
  }

  (data?.tasks || data?.taskOptions || []).slice(0, 3).forEach((task) => {
    pushAgentCitation(citations, {
      sourceType: 'live_pmapp',
      label: task.taskNumber ? `Task ${task.taskNumber}` : task.title,
      detail: [
        task.projectNumber ? `project ${task.projectNumber}` : null,
        task.status,
        task.dueDate ? `due ${task.dueDate}` : null,
      ].filter(Boolean).join(' · ') || null,
      routePath: task.routePath || '/tasks',
    }, seen);
  });

  (data?.certificates || data?.certificateOptions || []).slice(0, 3).forEach((certificate) => {
    pushAgentCitation(citations, {
      sourceType: 'live_pmapp',
      label: certificate.certificateNumber ? `Certificate ${certificate.certificateNumber}` : certificate.title,
      detail: [
        certificate.projectNumber ? `project ${certificate.projectNumber}` : null,
        certificate.issueDate ? `issued ${certificate.issueDate}` : null,
        certificate.address || certificate.suburb,
      ].filter(Boolean).join(' · ') || null,
      routePath: certificate.routePath || '/certificates',
    }, seen);
  });

  (data?.byda || data?.bydaOptions || []).slice(0, 3).forEach((record) => {
    pushAgentCitation(citations, {
      sourceType: 'live_pmapp',
      label: record.userReference || record.remoteExternalId || `BYDA ${record.bydaEnquiryId}`,
      detail: [
        record.projectNumber ? `project ${record.projectNumber}` : null,
        record.localStatus,
        record.address || record.suburb,
      ].filter(Boolean).join(' · ') || null,
      routePath: '/ai',
    }, seen);
  });

  if (data?.draftEmail) {
    pushAgentCitation(citations, {
      sourceType: 'draft',
      label: data.draftEmail.subject,
      detail: data.draftEmail.notes?.[0] || 'Draft artifact only',
      routePath: null,
    }, seen);
  }

  return citations.slice(0, 6);
}

function deriveAgentEntityHints(result: CompanyAiAgentRunResult): string[] {
  const querySpec = result.data?.querySpec;
  if (querySpec?.primaryIntent) {
    switch (querySpec.primaryIntent) {
      case 'find_projects':
        return ['project'];
      case 'find_clients':
        return ['client'];
      case 'find_tasks':
        return ['task'];
      case 'find_invoices':
        return ['invoice'];
      case 'find_quotations':
        return ['quotation'];
      case 'find_certificates':
        return ['certificate'];
      case 'find_byda':
        return ['byda'];
      case 'knowledge_lookup':
        return ['knowledge'];
      default:
        break;
    }
  }

  const hints = new Set<string>();
  if (result.data?.resolvedProject || (result.data?.projectOptions?.length || 0) > 0 || (result.data?.projects?.length || 0) > 0) {
    hints.add('project');
  }
  if (result.data?.resolvedClient || (result.data?.clientOptions?.length || 0) > 0 || (result.data?.clients?.length || 0) > 0) {
    hints.add('client');
  }
  if (result.data?.resolvedTask || (result.data?.taskOptions?.length || 0) > 0 || (result.data?.tasks?.length || 0) > 0) {
    hints.add('task');
  }
  if ((result.data?.quotations?.length || 0) > 0) {
    hints.add('quotation');
  }
  if (result.data?.resolvedCertificate || (result.data?.certificateOptions?.length || 0) > 0 || (result.data?.certificates?.length || 0) > 0) {
    hints.add('certificate');
  }
  if (result.data?.resolvedByda || (result.data?.bydaOptions?.length || 0) > 0 || (result.data?.byda?.length || 0) > 0) {
    hints.add('byda');
  }
  if ((result.data?.invoices?.length || 0) > 0 || result.data?.invoiceSummary || result.data?.draftEmail || result.data?.resolvedInvoice) {
    hints.add('invoice');
  }
  return Array.from(hints);
}

function mapAgentResultToQuickQueryResult(
  message: string,
  result: CompanyAiAgentRunResult
): CompanyAiQueryResult {
  const candidates = deriveAgentCandidates(result);
  const selectedEntity = deriveAgentSelectedEntity(result, candidates);
  const agentSteps = Array.isArray(result.steps)
    ? result.steps.map((step) => ({
        ...step,
        tool: step.tool || step.type || 'fetch',
        success: typeof step.success === 'boolean' ? step.success : step.type !== 'clarify',
      }))
    : [];
  const citations = deriveAgentCitations(result);
  const querySpec = result.data?.querySpec;
  const temporal = querySpec?.temporal?.[0];

  return {
    sessionId: result.sessionId,
    actionType: result.type === 'clarification' ? 'clarify' : 'answer',
    answer: result.answer,
    confidence: null,
    clarificationQuestion: result.type === 'clarification' ? result.answer : null,
    candidates,
    selectedEntityType: selectedEntity.selectedEntityType,
    selectedEntityId: selectedEntity.selectedEntityId,
    stateSummary: {
      currentScope: selectedEntity.selectedEntityType,
      clarificationCount: result.type === 'clarification' ? 1 : 0,
      selectedEntityType: selectedEntity.selectedEntityType,
      selectedEntityId: selectedEntity.selectedEntityId,
      candidateCount: candidates.length,
    },
    plan: {
      normalizedQuery: message.trim().toLowerCase(),
      entityTypeHints: deriveAgentEntityHints(result),
      intent: querySpec?.primaryIntent || 'agent_query',
      constraints: {
        filters: querySpec?.filters || {},
        relations: querySpec?.relations || {},
        aggregation: querySpec?.aggregation || {},
      },
      dateRange: {
        startDate: temporal?.start || null,
        endDate: temporal?.end || null,
        dateField: temporal?.field || null,
        sourcePhrase: temporal?.rawText || temporal?.relativeLabel || null,
      },
      relationHops: querySpec?.relations ? Object.keys(querySpec.relations).filter((key) => Boolean((querySpec.relations as Record<string, unknown>)[key])) : [],
      sortPreference: querySpec?.aggregation?.sortBy || 'grounded',
    },
    agentSteps,
    agentData: result.data,
    citations,
  };
}

const aiCommandFallbacks: Record<string, string> = {
  'ai-chat': 'ai.chat',
  'ai-file-preview': 'ai.file.preview',
  'ai-file-commit': 'ai.file.commit',
  'ai-knowledge-enqueue': 'ai.knowledge.enqueue',
  'ai-quota-upsert': 'ai.quota.upsert',
};

function isUnknownCommandError(error: unknown, commandName: string): boolean {
  const details = (error as any)?.details as Record<string, any> | null | undefined;
  const errorCode = details?.error?.code || details?.code || null;
  const message = String((error as any)?.message || details?.error?.message || details?.message || '');
  return errorCode === 'command_not_found' && message.includes(`Unknown command: ${commandName}`);
}

function isRouteNotFoundError(error: unknown): boolean {
  return Number((error as any)?.status || 0) === 404;
}

async function parseError(response: Response): Promise<Error> {
  if (response.status === 401) {
    tryStandbyReauth(getCurrentReturnTo());
  }

  const raw = await response.text();
  let body: any = null;
  try {
    body = raw ? JSON.parse(raw) : null;
  } catch {
    body = raw;
  }

  const message = body?.error?.message || body?.message || raw || `HTTP ${response.status}`;
  const error = new Error(message);
  (error as any).status = response.status;
  (error as any).details = body;
  return error;
}

class CompanyAiService {
  private readonly browserClient = new BrowserSessionApiClient(API_BASE_URL);

  private async requestAiCommand<T>(commandName: string, options: ApiRequestOptions): Promise<T> {
    try {
      return await this.browserClient.request<T>(`/api/v1/commands/${commandName}`, options);
    } catch (error) {
      const fallbackCommandName = aiCommandFallbacks[commandName];
      const shouldRetryWithLegacyRoute = Boolean(
        fallbackCommandName
        && (isUnknownCommandError(error, commandName) || isRouteNotFoundError(error))
      );

      if (!shouldRetryWithLegacyRoute) {
        throw error;
      }

      return this.browserClient.request<T>(`/api/v1/commands/${fallbackCommandName}`, options);
    }
  }

  async getUsage(): Promise<CompanyAiUsage> {
    const response = await this.browserClient.request('/api/v1/ai/usage/me');
    return unwrapData<CompanyAiUsage>(response);
  }

  async listConversations(): Promise<CompanyAiConversationSummary[]> {
    const response = await this.browserClient.request('/api/v1/ai/conversations');
    return unwrapData<CompanyAiConversationSummary[]>(response);
  }

  async getConversation(conversationId: number): Promise<CompanyAiConversationDetail> {
    const response = await this.browserClient.request(`/api/v1/ai/conversations/${conversationId}`);
    return unwrapData<CompanyAiConversationDetail>(response);
  }

  async listKnowledgeSources(): Promise<CompanyAiKnowledgeSource[]> {
    const response = await this.browserClient.request('/api/v1/ai/knowledge-sources');
    return unwrapData<CompanyAiKnowledgeSource[]>(response);
  }

  async listKnowledgeSourceFiles(sourceId: number): Promise<CompanyAiKnowledgeSourceFile[]> {
    const response = await this.browserClient.request(`/api/v1/ai/knowledge-sources/${sourceId}/files`);
    return unwrapData<CompanyAiKnowledgeSourceFile[]>(response);
  }

  async browseKnowledgeSharePoint(input?: {
    sharePointSiteId?: string;
    driveId?: string;
    folderPath?: string | null;
  }): Promise<CompanyAiSharePointBrowserResult> {
    const params = new URLSearchParams();
    if (input?.sharePointSiteId?.trim()) params.set('sharePointSiteId', input.sharePointSiteId.trim());
    if (input?.driveId?.trim()) params.set('driveId', input.driveId.trim());
    if (input?.folderPath?.trim()) params.set('folderPath', input.folderPath.trim());
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await this.browserClient.request(`/api/v1/ai/knowledge-sharepoint/browse${suffix}`);
    return unwrapData<CompanyAiSharePointBrowserResult>(response);
  }

  async discoverKnowledgeSharePointFolderFiles(input: {
    driveId?: string;
    folderPath: string;
  }): Promise<CompanyAiSharePointFolderFile[]> {
    const params = new URLSearchParams();
    if (input.driveId?.trim()) params.set('driveId', input.driveId.trim());
    params.set('folderPath', input.folderPath.trim());
    const response = await this.browserClient.request(`/api/v1/ai/knowledge-sharepoint/files?${params.toString()}`);
    return unwrapData<CompanyAiSharePointFolderFile[]>(response);
  }

  async listKnowledgeFiles(input?: {
    query?: string;
    sourceId?: number | null;
  }): Promise<CompanyAiKnowledgeFile[]> {
    const params = new URLSearchParams();
    if (input?.query?.trim()) {
      params.set('q', input.query.trim());
    }
    if (input?.sourceId && input.sourceId > 0) {
      params.set('sourceId', String(input.sourceId));
    }
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await this.browserClient.request(`/api/v1/ai/knowledge-files${suffix}`);
    return unwrapData<CompanyAiKnowledgeFile[]>(response);
  }

  async listOnlineQuoteFileGroups(input?: {
    query?: string;
    referralLeadId?: number | null;
    limit?: number;
  }): Promise<CompanyAiOnlineQuoteFileGroup[]> {
    const params = new URLSearchParams();
    if (input?.query?.trim()) {
      params.set('q', input.query.trim());
    }
    if (input?.referralLeadId && input.referralLeadId > 0) {
      params.set('referralLeadId', String(input.referralLeadId));
    }
    if (input?.limit && input.limit > 0) {
      params.set('limit', String(input.limit));
    }
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await this.browserClient.request(`/api/v1/referral-quote-files${suffix}`);
    return unwrapData<CompanyAiOnlineQuoteFileGroup[]>(response);
  }

  async inferQuotationProjectDescription(input: {
    userDescription?: string | null;
    helpType?: string | null;
    projectType?: string | null;
  }): Promise<CompanyAiQuotationDescriptionResult> {
    const response = await this.browserClient.request('/api/v1/referral-quote-files/project-description', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return unwrapData<CompanyAiQuotationDescriptionResult>(response);
  }

  async previewGrounding(input: {
    message: string;
    sourceScope: 'company' | 'project' | 'selected';
    surface?: CompanyAiChatSurface;
    selectedSourceIds?: number[];
    conversationTail?: Array<{
      role: string;
      messageText: string;
    }>;
  }): Promise<CompanyAiGroundingPreview> {
    const response = await this.browserClient.request('/api/v1/ai/grounding-preview', {
      method: 'POST',
      body: {
        message: input.message,
        sourceScope: input.sourceScope,
        surface: input.surface || 'workspace',
        selectedSourceIds: input.selectedSourceIds || [],
        conversationTail: input.conversationTail || [],
      },
    });

    return unwrapData<CompanyAiGroundingPreview>(response);
  }

  async listAdminQuotas(): Promise<CompanyAiUsage[]> {
    const response = await this.browserClient.request('/api/v1/ai/admin/quotas');
    return unwrapData<CompanyAiUsage[]>(response);
  }

  async getModelSettings(): Promise<CompanyAiModelSettings> {
    const response = await this.browserClient.request('/api/v1/ai/admin/model-settings');
    return unwrapData<CompanyAiModelSettings>(response);
  }

  async updateModelSettings(input: {
    pmQueryModelSize: CompanyAiPmQueryModelSize;
    pmQueryIntentProvider?: CompanyAiPmQueryIntentProvider;
  }): Promise<CompanyAiModelSettings> {
    const response = await this.browserClient.request('/api/v1/ai/admin/model-settings', {
      method: 'PUT',
      body: input,
    });
    return unwrapData<CompanyAiModelSettings>(response);
  }

  async getEntityGraphVisualization(input?: {
    entityType?: string;
    entityId?: string;
    query?: string;
    limit?: number;
  }): Promise<CompanyAiGraphVisualization> {
    const params = new URLSearchParams();
    if (input?.entityType?.trim() && input.entityType !== 'all') params.set('entityType', input.entityType.trim());
    if (input?.entityId?.trim()) params.set('entityId', input.entityId.trim());
    if (input?.query?.trim()) params.set('q', input.query.trim());
    if (input?.limit && input.limit > 0) params.set('limit', String(input.limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await this.browserClient.request(`/api/v1/ai/entity-graph/visualization${suffix}`);
    return unwrapData<CompanyAiGraphVisualization>(response);
  }

  async listEmailReviewItems(input?: {
    classification?: string;
    minConfidence?: number | null;
    matchedProjectId?: number | null;
    queueMode?: 'exceptions' | 'all';
    reviewStatus?: string;
    sourceMailbox?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CompanyEmailReviewListResult> {
    const params = new URLSearchParams();
    if (input?.classification?.trim()) params.set('classification', input.classification.trim());
    if (typeof input?.minConfidence === 'number') params.set('minConfidence', String(input.minConfidence));
    if (input?.matchedProjectId && input.matchedProjectId > 0) params.set('matchedProjectId', String(input.matchedProjectId));
    params.set('queueMode', input?.queueMode || 'exceptions');
    if (input?.reviewStatus?.trim()) params.set('reviewStatus', input.reviewStatus.trim());
    if (input?.sourceMailbox?.trim()) params.set('sourceMailbox', input.sourceMailbox.trim());
    params.set('page', String(input?.page || 1));
    params.set('pageSize', String(input?.pageSize || 20));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await this.browserClient.request(`/api/v1/email/messages${suffix}`);
    return unwrapData<CompanyEmailReviewListResult>(response);
  }

  async getEmailReviewItem(emailMessageId: number): Promise<CompanyEmailReviewDetail> {
    const response = await this.browserClient.request(`/api/v1/email/messages/${emailMessageId}`);
    return unwrapData<CompanyEmailReviewDetail>(response);
  }

  async reprocessEmailMessage(emailMessageId: number): Promise<CompanyEmailReprocessResult> {
    const response = await this.browserClient.request(`/api/v1/email/messages/${emailMessageId}/reprocess`, {
      method: 'POST',
    });
    return unwrapData<CompanyEmailReprocessResult>(response);
  }

  async reviewEmailMessage(input: {
    emailMessageId: number;
    reviewStatus: CompanyEmailReviewStatus;
    reviewNotes?: string;
  }): Promise<Record<string, unknown>> {
    const response = await this.browserClient.request(`/api/v1/email/messages/${input.emailMessageId}/review`, {
      method: 'POST',
      body: {
        reviewStatus: input.reviewStatus,
        reviewNotes: input.reviewNotes || undefined,
      },
    });
    return unwrapData<Record<string, unknown>>(response);
  }

  async reviewEmailSuggestion(input: {
    kind: 'project' | 'task' | 'file';
    suggestionId: number;
    reviewStatus: CompanyEmailReviewStatus;
    reviewNotes?: string;
  }): Promise<Record<string, unknown>> {
    const response = await this.browserClient.request(`/api/v1/email/suggestions/${input.kind}/${input.suggestionId}/review`, {
      method: 'POST',
      body: {
        reviewStatus: input.reviewStatus,
        reviewNotes: input.reviewNotes || undefined,
      },
    });
    return unwrapData<Record<string, unknown>>(response);
  }

  async createTaskFromEmailSuggestion(input: {
    suggestionId: number;
    title?: string;
    description?: string;
    dueDate?: string | null;
    priority?: 'low' | 'medium' | 'high' | null;
  }): Promise<CompanyEmailTaskCreateResult> {
    const response = await this.browserClient.request(`/api/v1/email/suggestions/task/${input.suggestionId}/create-task`, {
      method: 'POST',
      body: {
        title: input.title || undefined,
        description: input.description || undefined,
        dueDate: input.dueDate || undefined,
        priority: input.priority || undefined,
      },
    });
    return unwrapData<CompanyEmailTaskCreateResult>(response);
  }

  async getEmailDraftAssistance(emailMessageId: number): Promise<CompanyEmailDraftAssistance> {
    const response = await this.browserClient.request(`/api/v1/email/messages/${emailMessageId}/draft-assistance`);
    return unwrapData<CompanyEmailDraftAssistance>(response);
  }

  async chat(input: {
    conversationId?: number | null;
    message: string;
    sourceScope: 'company' | 'project' | 'selected';
    surface: CompanyAiChatSurface;
    selectedSourceIds?: number[];
    conversationTail?: Array<{
      role: string;
      messageText: string;
    }>;
  }): Promise<CompanyAiChatResult> {
    const response = await this.requestAiCommand('ai-chat', {
      method: 'POST',
      headers: {
        'Idempotency-Key': buildIdempotencyKey('ai.chat'),
      },
      body: {
        conversationId: input.conversationId ?? undefined,
        message: input.message,
        sourceScope: input.sourceScope,
        surface: input.surface,
        selectedSourceIds: input.selectedSourceIds || [],
        conversationTail: input.conversationTail || [],
      },
    });

    return unwrapData<CompanyAiChatResult>(response);
  }

  async aiSearch(input: {
    query: string;
    surface: CompanyAiChatSurface;
  }): Promise<CompanyAiSearchResult> {
    const response = await this.browserClient.request('/api/v1/ai/search', {
      method: 'POST',
      body: input,
    });
    return unwrapData<CompanyAiSearchResult>(response);
  }

  async startAiQuery(input: {
    message: string;
    surface?: CompanyAiChatSurface;
    featureFlags?: Record<string, unknown>;
  }): Promise<CompanyAiQueryResult> {
    const response = await this.browserClient.request('/api/v1/ai-query/start', {
      method: 'POST',
      body: input,
    });
    return unwrapData<CompanyAiQueryResult>(response);
  }

  async queryAgent(input: {
    message: string;
    sessionId?: string;
    currentEntity?: CompanyAiAgentCurrentEntity;
    pmQueryModelSize?: CompanyAiPmQueryModelSize;
    pmQueryIntentProvider?: CompanyAiPmQueryIntentProvider;
  }): Promise<CompanyAiQueryResult> {
    const response = await this.browserClient.request('/api/v1/agent/query', {
      method: 'POST',
      body: {
        message: input.message,
        sessionId: asNonEmptyString(input.sessionId) || undefined,
        pmQueryModelSize: input.pmQueryModelSize,
        pmQueryIntentProvider: input.pmQueryIntentProvider,
        currentEntity: input.currentEntity
          ? {
              type: String(input.currentEntity.type || '').trim(),
              id: String(input.currentEntity.id || '').trim(),
              label: asNonEmptyString(input.currentEntity.label),
            }
          : undefined,
      },
    });
    return mapAgentResultToQuickQueryResult(input.message, unwrapData<CompanyAiAgentRunResult>(response));
  }

  async continueAiQuery(input: {
    sessionId: string;
    message: string;
  }): Promise<CompanyAiQueryResult> {
    const response = await this.browserClient.request('/api/v1/ai-query/continue', {
      method: 'POST',
      body: input,
    });
    return unwrapData<CompanyAiQueryResult>(response);
  }

  async getAiQuerySession(sessionId: string): Promise<CompanyAiQuerySessionDetail> {
    const response = await this.browserClient.request(`/api/v1/ai-query/session/${encodeURIComponent(sessionId)}`);
    return unwrapData<CompanyAiQuerySessionDetail>(response);
  }

  async uploadAiFile(file: File): Promise<{ fileId: string; fileName: string; contentPath: string }> {
    const response = await fetch(`${API_BASE_URL}/v1/draft-files`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'x-draft-entity-type': 'ai-upload',
        'x-draft-file-name': file.name,
        'content-type': file.type || 'application/octet-stream',
      },
      body: file,
    });

    if (!response.ok) {
      throw await parseError(response);
    }

    const body = await response.json();
    return unwrapData(body);
  }

  async previewFile(input: {
    draftFileId: string;
    userHint?: string;
  }): Promise<CompanyAiFilePreview> {
    const response = await this.requestAiCommand('ai-file-preview', {
      method: 'POST',
      headers: {
        'Idempotency-Key': buildIdempotencyKey('ai.file.preview'),
      },
      body: input,
    });

    return unwrapData<CompanyAiFilePreview>(response);
  }

  async commitFile(input: {
    previewResultId: number;
    overrideDriveId?: string;
    overrideFolderPath?: string;
    overrideFileName?: string;
  }): Promise<Record<string, unknown>> {
    const response = await this.requestAiCommand('ai-file-commit', {
      method: 'POST',
      headers: {
        'Idempotency-Key': buildIdempotencyKey('ai.file.commit'),
      },
      body: input,
    });

    return unwrapData<Record<string, unknown>>(response);
  }

  async enqueueKnowledgeSource(input: {
    aiKnowledgeSourceId?: number;
    sourceType: string;
    scopeType?: string;
    scopeRef?: string;
    title: string;
    description?: string;
    sharePointSiteId?: string;
    driveId?: string;
    folderPath?: string;
    filePath?: string;
    draftFileId?: string;
    sourceUrl?: string;
    selectedSharePointFiles?: Array<{
      itemId?: string;
      fileName?: string;
      relativePath?: string;
      sourceFilePath?: string;
      sourceUrl?: string;
      contentType?: string;
      sizeBytes?: number;
    }>;
    selectedUploadedFiles?: Array<{
      draftFileId: string;
      relativePath?: string;
    }>;
    keepExistingFiles?: boolean;
    autoIndex?: boolean;
  }): Promise<Record<string, unknown>> {
    const response = await this.requestAiCommand('ai-knowledge-enqueue', {
      method: 'POST',
      headers: {
        'Idempotency-Key': buildIdempotencyKey('ai.knowledge.enqueue'),
      },
      body: input,
    });

    return unwrapData<Record<string, unknown>>(response);
  }

  async ingestKnowledgeSource(input: {
    aiKnowledgeSourceId: number;
    aiKnowledgeSourceFileIds?: number[];
  }): Promise<{
    knowledgeIngestionRunId: number;
    fileCount: number;
    documentCount: number;
    knowledgeDocumentIds: number[];
  }> {
    const response = await this.browserClient.request('/api/v1/knowledge/ingest', {
      method: 'POST',
      body: input,
    });
    return unwrapData(response);
  }

  async reindexKnowledgeSource(aiKnowledgeSourceId: number): Promise<Record<string, unknown>> {
    const response = await this.browserClient.request(`/api/v1/ai/knowledge-sources/${aiKnowledgeSourceId}/reindex`, {
      method: 'POST',
    });
    return unwrapData<Record<string, unknown>>(response);
  }

  async listKnowledgeDocuments(input?: {
    query?: string;
    aiKnowledgeSourceId?: number | null;
  }): Promise<CompanyKnowledgeDocumentSummary[]> {
    const params = new URLSearchParams();
    if (input?.query?.trim()) params.set('q', input.query.trim());
    if (input?.aiKnowledgeSourceId && input.aiKnowledgeSourceId > 0) params.set('aiKnowledgeSourceId', String(input.aiKnowledgeSourceId));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await this.browserClient.request(`/api/v1/knowledge/documents${suffix}`);
    return unwrapData<CompanyKnowledgeDocumentSummary[]>(response);
  }

  async getKnowledgeDocument(documentId: number): Promise<CompanyKnowledgeDocumentSummary> {
    const response = await this.browserClient.request(`/api/v1/knowledge/documents/${documentId}`);
    return unwrapData<CompanyKnowledgeDocumentSummary>(response);
  }

  async getKnowledgeDocumentStructure(documentId: number): Promise<CompanyKnowledgeDocumentStructure> {
    const response = await this.browserClient.request(`/api/v1/knowledge/documents/${documentId}/structure`);
    return unwrapData<CompanyKnowledgeDocumentStructure>(response);
  }

  async searchKnowledgeDocuments(query: string): Promise<CompanyKnowledgeSearchResult> {
    const response = await this.browserClient.request(`/api/v1/knowledge/search?q=${encodeURIComponent(query)}`);
    return unwrapData<CompanyKnowledgeSearchResult>(response);
  }

  async upsertQuota(input: {
    ownerEmail: string;
    monthlyTokenLimit: number;
    isEnabled: boolean;
    notes?: string;
  }): Promise<Record<string, unknown>> {
    const response = await this.requestAiCommand('ai-quota-upsert', {
      method: 'POST',
      headers: {
        'Idempotency-Key': buildIdempotencyKey('ai.quota.upsert'),
      },
      body: input,
    });

    return unwrapData<Record<string, unknown>>(response);
  }

  async executeSuggestedAction(commandName: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.browserClient.request(`/api/v1/commands/${commandName}`, {
      method: 'POST',
      headers: {
        'Idempotency-Key': buildIdempotencyKey(commandName),
      },
      body: payload,
    });

    return unwrapData<Record<string, unknown>>(response);
  }
}

export const companyAiService = new CompanyAiService();
